import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { MessageSquare, ArrowRight, ShieldCheck, Zap, Layers, GitBranch, Lock } from 'lucide-react';
import { Reveal, Stagger, StaggerItem, EASE } from './LandingMotion';
import { IntelligentBackdrop } from './IntelligentBackdrop';

export const PipelineSection = () => {
  const reduce = useReducedMotion();
  return (
    <section className="py-24 md:py-32 relative overflow-hidden bg-white">
      <IntelligentBackdrop />
      <div className="w-full max-w-[1200px] mx-auto px-6 relative z-10">

        {/* Section Header */}
        <div className="max-w-3xl mb-20">
            <Reveal blur>
                <div className="flex items-center gap-2 text-orange-500 font-mono text-xs uppercase tracking-widest mb-4">
                    <GitBranch size={14} />
                    <span>Agentic Automation</span>
                </div>
                <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-[#1C1C1E] mb-6 leading-tight">
                It doesn’t just answer. <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-200">
                    It does the work.
                </span>
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed font-light max-w-2xl">
                Bolt turns a plain-English request into finished work — scheduling, planning, reporting, assigning — and previews every change before it happens. You decide how much it handles on its own.
                </p>
            </Reveal>
        </div>

        {/* Pipeline Visualization Steps */}
        <div className="relative">
            {/* Connecting Line (Desktop) — draws in on scroll, left → right */}
            <motion.div
                className="hidden lg:block absolute top-12 left-0 w-full h-px bg-gradient-to-r from-orange-500/0 via-orange-500/30 to-orange-500/0"
                style={{ transformOrigin: '0% 50%' }}
                initial={reduce ? { opacity: 0 } : { opacity: 0, scaleX: 0 }}
                whileInView={
                    reduce
                        ? { opacity: 1, transition: { duration: 0.3 } }
                        : { opacity: 1, scaleX: 1, transition: { duration: 1, ease: EASE } }
                }
                viewport={{ once: true, margin: '-80px' }}
            />

            {/* Subtle directional pulse: a faint dot traveling Ask → draft → done.
                A full-width track (the motion element) slides via transform x from -100% → 0%,
                carrying a dot pinned at its right edge fully across the connector. % is of the
                track's own width = full traversal. Transform-only; off under reduced motion. */}
            {!reduce && (
                <div className="hidden lg:block absolute top-12 left-0 w-full h-px pointer-events-none overflow-visible">
                    <motion.div
                        className="absolute top-0 left-0 h-px w-full"
                        initial={{ x: '-100%', opacity: 0 }}
                        animate={{ x: ['-100%', '0%'], opacity: [0, 0.6, 0.6, 0] }}
                        transition={{ duration: 3.6, ease: 'easeInOut', repeat: Infinity, repeatDelay: 1.6 }}
                    >
                        <span className="absolute right-0 top-1/2 -mt-1 h-2 w-2 rounded-full bg-orange-400/70 blur-[1px]" />
                    </motion.div>
                </div>
            )}

            <Stagger className="grid grid-cols-1 lg:grid-cols-3 gap-8" stagger={0.16} delayChildren={0.1}>
            {[
                {
                    step: '01',
                    title: 'Ask',
                    subtitle: 'In plain English',
                    icon: MessageSquare,
                    desc: '“Schedule a toolbox talk Thursday.” “Draft tomorrow’s plan from the blueprints.” “Pull the overdue RFIs.” Type it or say it.',
                    features: ['Voice or text', 'Knows your crew & docs', 'No forms to fill'],
                    color: 'text-orange-400',
                    bg: 'bg-orange-500/10',
                    border: 'border-orange-500/20'
                },
                {
                    step: '02',
                    title: 'Bolt drafts it',
                    subtitle: 'You see it first',
                    icon: Zap,
                    desc: 'Bolt assembles the meeting, the daily plan, or the report from your live data — and shows you a preview before anything changes.',
                    features: ['Multi-step planning', 'Grounded in your data', 'Preview before commit'],
                    color: 'text-orange-400',
                    bg: 'bg-orange-500/10',
                    border: 'border-orange-500/20'
                },
                {
                    step: '03',
                    title: 'Approve → done',
                    subtitle: 'You stay in control',
                    icon: ShieldCheck,
                    desc: 'One tap and Bolt executes: books the meeting, files the report, assigns the training. You set how much it can do on its own.',
                    features: ['One-tap approve', 'Autonomy levels', 'Full audit trail'],
                    color: 'text-orange-400',
                    bg: 'bg-orange-500/10',
                    border: 'border-orange-500/20'
                }
            ].map((item, i) => (
                <StaggerItem
                    key={i}
                    y={30}
                    className="relative group"
                    whileHover={reduce ? undefined : { y: -4, transition: { duration: 0.2, ease: EASE } }}
                >
                    {/* Step Number — faint fade-in */}
                    <motion.div
                        className={`text-6xl font-bold text-black/5 absolute -top-10 -right-4 font-mono select-none`}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: reduce ? 0.3 : 0.9, ease: EASE, delay: reduce ? 0 : 0.15 }}
                    >
                        {item.step}
                    </motion.div>

                    <div className="bg-white border border-black/[0.07] rounded-2xl vh-edge-light p-8 h-full hover:border-black/15 transition-colors relative z-10">
                        {/* Icon */}
                        <div className={`w-14 h-14 rounded-xl ${item.bg} ${item.border} border flex items-center justify-center mb-6 shadow-lg`}>
                            <item.icon className={item.color} size={28} />
                        </div>

                        <h3 className="text-xl font-bold text-[#1C1C1E] mb-1">{item.title}</h3>
                        <p className={`text-xs uppercase tracking-wider font-semibold mb-4 ${item.color}`}>{item.subtitle}</p>

                        <p className="text-slate-600 text-sm leading-relaxed mb-6 border-b border-black/[0.06] pb-6">
                            {item.desc}
                        </p>

                        <motion.ul
                            className="space-y-3"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: '-80px' }}
                            variants={{
                                hidden: {},
                                visible: { transition: { staggerChildren: reduce ? 0 : 0.07, delayChildren: reduce ? 0 : 0.12 } },
                            }}
                        >
                            {item.features.map((feature, f) => (
                                <motion.li
                                    key={f}
                                    className="flex items-center gap-3 text-sm text-slate-600"
                                    variants={{
                                        hidden: reduce ? { opacity: 0 } : { opacity: 0, y: 10 },
                                        visible: { opacity: 1, y: 0, transition: { duration: reduce ? 0.2 : 0.5, ease: EASE } },
                                    }}
                                >
                                    <div className={`w-1.5 h-1.5 rounded-full ${item.bg.replace('/10', '/50')}`} />
                                    {feature}
                                </motion.li>
                            ))}
                        </motion.ul>
                    </div>
                </StaggerItem>
            ))}
            </Stagger>
        </div>

      </div>
    </section>
  );
};
